import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgData } from '../../hooks/useOrgData';
import { useOrgSettings } from '../../hooks/useOrgSettings';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth(); // 0-indexed

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// One color per year slot: index 0 = oldest, last = most recent
const YEAR_COLORS = ['#94a3b8', '#f59e0b', '#3b82f6']; // slate → amber → blue

const useTxByYear = (orgId, year) => {
  const [tx, setTx] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, 'orgs', orgId, 'transactions'), where('year', '==', year));
    const unsub = onSnapshot(q, (snap) => {
      setTx(snap.docs.map(d => d.data()));
    });
    return unsub;
  }, [orgId, year]);
  return tx;
};

const monthlyTotals = (tx) =>
  MONTHS.map((_, i) => {
    const m = String(i + 1).padStart(2, '0');
    const monthTx = tx.filter(t => t.type !== 'Starting Balance' && (t.stmtMonth || t.date || '').substring(5, 7) === m);
    return {
      income:  monthTx.reduce((s, t) => s + (t.income  || 0), 0),
      expense: monthTx.reduce((s, t) => s + (t.expense || 0), 0),
    };
  });

// ── Multi-year Bar Chart ───────────────────────────────────────────────────────

const MultiYearBarChart = ({ yearlyData }) => {
  const [tooltip, setTooltip] = useState(null);

  const activeYears = yearlyData.filter(yd =>
    yd.monthly.some(m => m.income > 0 || m.expense > 0)
  );
  if (activeYears.length === 0) return null;

  const halfH      = 130;
  const barW       = 5;  // px — fixed bar width
  const barGap     = 2;  // px — gap between bars within a month
  const maxIncome  = Math.max(...activeYears.flatMap(yd => yd.monthly.map(m => m.income)),  1);
  const maxExpense = Math.max(...activeYears.flatMap(yd => yd.monthly.map(m => m.expense)), 1);

  const colorFor = (idx) => YEAR_COLORS[YEAR_COLORS.length - activeYears.length + idx];
  const fmtK = (n) => n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${Math.round(n)}`;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Monthly Cash Flow</h3>
        <div className="flex gap-4 text-xs text-slate-500">
          {activeYears.map((yd, idx) => (
            <span key={yd.year} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm inline-block" style={{ background: colorFor(idx) }} />
              {yd.year}
            </span>
          ))}
        </div>
      </div>

      {/* Chart + y-axis */}
      <div className="flex gap-2">
        {/* Y-axis labels */}
        <div className="w-12 shrink-0 flex flex-col justify-between text-right pr-1" style={{ height: halfH * 2 }}>
          <span className="text-xs text-slate-500 leading-none">{fmtK(maxIncome)}</span>
          <span className="text-xs text-slate-500 leading-none">$0</span>
          <span className="text-xs text-slate-500 leading-none">({fmtK(maxExpense)})</span>
        </div>

        {/* Bars */}
        <div className="flex-1 relative">
          {/* Zero line */}
          <div className="absolute left-0 right-0 border-t-2 border-slate-300 z-10" style={{ top: halfH }} />

          {/* Tooltip */}
          {tooltip && (
            <div
              className="absolute z-20 bg-slate-800 text-white text-xs rounded-lg px-2.5 py-1.5 pointer-events-none whitespace-nowrap shadow-lg"
              style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -110%)' }}
            >
              <div className="font-semibold">{tooltip.label}</div>
              <div>{tooltip.value}</div>
            </div>
          )}

          <div className="flex" style={{ height: halfH * 2 }}>
            {MONTHS.map((month, mi) => (
              <div key={mi} className="flex-1 flex flex-col items-center">
                {/* Upper half — income */}
                <div className="flex items-end" style={{ height: halfH, gap: barGap }}>
                  {activeYears.map((yd, idx) => {
                    const income = yd.monthly[mi].income;
                    const h = Math.round((income / maxIncome) * halfH);
                    return (
                      <div key={yd.year} style={{ width: barW }} className="flex flex-col justify-end h-full">
                        {h > 0 && (
                          <div
                            onMouseEnter={e => setTooltip({
                              x: e.currentTarget.getBoundingClientRect().left - e.currentTarget.closest('.flex-1.relative').getBoundingClientRect().left + barW / 2,
                              y: e.currentTarget.getBoundingClientRect().top  - e.currentTarget.closest('.flex-1.relative').getBoundingClientRect().top,
                              label: `${yd.year} Income`,
                              value: `$${income.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                            })}
                            onMouseLeave={() => setTooltip(null)}
                            style={{ height: h, background: colorFor(idx), borderRadius: '2px 2px 0 0', cursor: 'default' }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Lower half — expense */}
                <div className="flex items-start" style={{ height: halfH, gap: barGap }}>
                  {activeYears.map((yd, idx) => {
                    const expense = yd.monthly[mi].expense;
                    const h = Math.round((expense / maxExpense) * halfH);
                    return (
                      <div key={yd.year} style={{ width: barW }} className="flex flex-col justify-start h-full">
                        {h > 0 && (
                          <div
                            onMouseEnter={e => setTooltip({
                              x: e.currentTarget.getBoundingClientRect().left - e.currentTarget.closest('.flex-1.relative').getBoundingClientRect().left + barW / 2,
                              y: e.currentTarget.getBoundingClientRect().top  - e.currentTarget.closest('.flex-1.relative').getBoundingClientRect().top,
                              label: `${yd.year} Expenses`,
                              value: `$${expense.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                            })}
                            onMouseLeave={() => setTooltip(null)}
                            style={{ height: h, background: colorFor(idx), opacity: 0.5, borderRadius: '0 0 2px 2px', cursor: 'default' }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Month labels */}
      <div className="flex mt-1.5 pl-14">
        {MONTHS.map((month, mi) => (
          <div key={mi} className={`flex-1 text-center text-xs ${
            mi === CURRENT_MONTH ? 'font-bold text-slate-700' : 'text-slate-500'
          }`}>
            {month}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Per-Account Balance Tiles ─────────────────────────────────────────────────

const computeAccountBalances = (allTx, activeAccounts, asOfDate) => {
  const tx = allTx.filter(t => t.date <= asOfDate);
  const startingTx = tx.filter(t => t.type === 'Starting Balance');
  const maxSbYear  = startingTx.length > 0 ? Math.max(...startingTx.map(t => t.year || 0)) : 0;
  const openingTx  = startingTx.filter(t => t.year === maxSbYear);
  const activeTx   = tx.filter(t => t.type !== 'Starting Balance');

  const balances = {};
  activeAccounts.forEach(a => {
    const opening = openingTx.filter(t => t.account === a.name).reduce((s, t) => s + (t.income || 0), 0);
    const inc     = activeTx.filter(t => t.account === a.name).reduce((s, t) => s + (t.income  || 0), 0);
    const exp     = activeTx.filter(t => t.account === a.name).reduce((s, t) => s + (t.expense || 0), 0);
    balances[a.name] = opening + inc - exp;
  });
  return balances;
};

const AccountTile = ({ name, current, lastYear }) => {
  const change = lastYear !== 0 ? ((current - lastYear) / Math.abs(lastYear)) * 100 : null;
  const isUp = change !== null && change >= 0;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1 truncate">{name}</div>
      <div className={`text-2xl md:text-3xl font-bold tracking-tight ${current < 0 ? 'text-red-600' : 'text-slate-800'}`}>
        {fmt(current)}
      </div>
      {change !== null && (
        <div className={`inline-flex items-center gap-1 mt-2 text-xs font-semibold ${isUp ? 'text-green-700' : 'text-red-600'}`}>
          <span>{isUp ? '▲' : '▼'}</span>
          <span>{Math.abs(change).toFixed(1)}%</span>
          <span className="font-normal text-slate-500 ml-0.5">vs. last year</span>
        </div>
      )}
    </div>
  );
};

const AccountTiles = ({ allTx, activeAccounts, todayStr, lastYearStr }) => {
  const balancesNow      = computeAccountBalances(allTx, activeAccounts, todayStr);
  const balancesLastYear = computeAccountBalances(allTx, activeAccounts, lastYearStr);

  if (activeAccounts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {activeAccounts.map(a => (
        <AccountTile
          key={a.name}
          name={a.name}
          current={balancesNow[a.name] || 0}
          lastYear={balancesLastYear[a.name] || 0}
        />
      ))}
    </div>
  );
};

// ── Balance by Account & Fund ─────────────────────────────────────────────────

const BalanceSummary = ({ allTx, accounts, funds }) => {
  const activeAccounts = accounts.filter(a => a.active !== false);
  const activeFunds    = funds.filter(f => f.active !== false);

  const allStartingTx = allTx.filter(t => t.type === 'Starting Balance');
  // Only use the most recent year's starting balance per account to avoid double-counting
  const maxSbYear  = allStartingTx.length > 0 ? Math.max(...allStartingTx.map(t => t.year || 0)) : 0;
  const startingTx = allStartingTx.filter(t => t.year === maxSbYear);
  const activeTx   = allTx.filter(t => t.type !== 'Starting Balance');

  // Account balances — book (all tx) and cleared (cleared tx only)
  const acctBal = {};
  activeAccounts.forEach(a => {
    const opening    = startingTx.filter(t => t.account === a.name).reduce((s, t) => s + (t.income || 0), 0);
    const inc        = activeTx.filter(t => t.account === a.name).reduce((s, t) => s + (t.income  || 0), 0);
    const exp        = activeTx.filter(t => t.account === a.name).reduce((s, t) => s + (t.expense || 0), 0);
    const clearedInc = activeTx.filter(t => t.account === a.name && t.cleared).reduce((s, t) => s + (t.income  || 0), 0);
    const clearedExp = activeTx.filter(t => t.account === a.name && t.cleared).reduce((s, t) => s + (t.expense || 0), 0);
    acctBal[a.name] = {
      book:    opening + inc - exp,
      cleared: opening + clearedInc - clearedExp,
    };
  });

  // Fund balances
  const fundBal = {};
  activeFunds.forEach(f => { fundBal[f.name] = 0; });

  startingTx.forEach(t => {
    const fn = t.classification;
    if (fundBal[fn] === undefined) fundBal[fn] = 0;
    fundBal[fn] += t.income || 0;
  });

  activeTx.forEach(t => {
    if (t.split && t.lines?.length) {
      t.lines.forEach(line => {
        const fn = line.classification || t.classification;
        if (fundBal[fn] === undefined) fundBal[fn] = 0;
        if (t.income  > 0) fundBal[fn] += line.amount || 0;
        if (t.expense > 0) fundBal[fn] -= line.amount || 0;
      });
    } else {
      const fn = t.classification;
      if (fn) {
        if (fundBal[fn] === undefined) fundBal[fn] = 0;
        fundBal[fn] += (t.income || 0) - (t.expense || 0);
      }
    }
  });

  const totalBook = activeAccounts.reduce((s, a) => s + (acctBal[a.name]?.book || 0), 0);

  if (activeAccounts.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-700 text-white flex items-center justify-between">
        <h3 className="font-semibold text-sm">Current Balances</h3>
        <span className="text-sm font-bold">{fmt(totalBook)}</span>
      </div>
      <div className="px-4 py-1.5 flex justify-end bg-slate-50 border-b border-gray-100 text-xs font-semibold text-slate-500 uppercase tracking-wide">
        <span className="w-24 text-right">Current</span>
        <span className="hidden sm:block w-24 text-right ml-4">Bank</span>
      </div>
      <div className="divide-y divide-gray-100">
        {activeAccounts.map(acct => {
          const linkedFunds = activeFunds.filter(f => f.account === acct.name);
          const book        = acctBal[acct.name]?.book    || 0;
          const cleared     = acctBal[acct.name]?.cleared || 0;
          const outstanding = book - cleared;
          return (
            <div key={acct.name}>
              <div className="px-4 py-3 flex items-center justify-between bg-slate-50/60">
                <div className="min-w-0 flex-1 mr-3">
                  <span className="font-semibold text-slate-700 text-sm">{acct.name}</span>
                  {outstanding !== 0 && (
                    <span className="hidden sm:inline ml-2 text-xs text-slate-500">
                      {fmt(Math.abs(outstanding))} outstanding
                    </span>
                  )}
                </div>
                <div className="flex items-center flex-shrink-0">
                  <span className={`w-24 text-right font-bold text-sm ${book < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                    {fmt(book)}
                  </span>
                  <span className={`hidden sm:block w-24 text-right text-sm font-medium ml-4 ${cleared < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                    {fmt(cleared)}
                  </span>
                </div>
              </div>
              {linkedFunds.length > 1 && linkedFunds.map(f => {
                const bal = fundBal[f.name] || 0;
                return (
                  <div key={f.name} className="px-4 py-2 flex items-center justify-between pl-8 hover:bg-gray-50">
                    <span className="text-sm text-slate-500 min-w-0 flex-1 mr-3 truncate">{f.name}</span>
                    <div className="flex items-center flex-shrink-0">
                      <span className={`w-24 text-right text-sm font-medium ${bal < 0 ? 'text-red-500' : 'text-slate-600'}`}>
                        {fmt(bal)}
                      </span>
                      <span className="hidden sm:block w-24 ml-4" />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Dashboard ──────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const orgId = useOrgId();
  const { orgName } = useOrgSettings(orgId);
  const { accounts, funds } = useOrgData(orgId);

  // Load 3 years of data
  const tx0 = useTxByYear(orgId, CURRENT_YEAR - 2);
  const tx1 = useTxByYear(orgId, CURRENT_YEAR - 1);
  const tx2 = useTxByYear(orgId, CURRENT_YEAR);

  const yearlyData = [
    { year: CURRENT_YEAR - 2, tx: tx0, monthly: monthlyTotals(tx0) },
    { year: CURRENT_YEAR - 1, tx: tx1, monthly: monthlyTotals(tx1) },
    { year: CURRENT_YEAR,     tx: tx2, monthly: monthlyTotals(tx2) },
  ];

  // All years combined so prior-year uncleared transactions affect the balance
  const allTx = [...tx0, ...tx1, ...tx2];

  const activeAccounts = accounts.filter(a => a.active !== false);
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const lastYear = new Date(today);
  lastYear.setFullYear(lastYear.getFullYear() - 1);
  const lastYearStr = lastYear.toISOString().split('T')[0];

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <div className="mb-7">
        <h2 className="text-2xl font-bold text-slate-800">{orgName || 'Dashboard'}</h2>
      </div>

      <div className="mb-6">
        <AccountTiles allTx={allTx} activeAccounts={activeAccounts} todayStr={todayStr} lastYearStr={lastYearStr} />
      </div>

      <div className="mb-6">
        <BalanceSummary allTx={allTx} accounts={accounts} funds={funds} />
      </div>

      <MultiYearBarChart yearlyData={yearlyData} />
    </div>
  );
};

export default Dashboard;
